// hooks/useCreateNewReservation.ts
import { useDispatch, useSelector } from "react-redux";
import { apartmentBookingApiSlice } from "../../api/apartment/apartmentApiSlice";
import ErrorTracker from "../helpers/errorTracker";
import Logger from "../helpers/logger";
import {
  apartmentsLoadFailed,
  apartmentsLoadStarted,
  categoryNoticeSet,
  setAllApartment,
} from "../slice/apartmentRental/apartmentRentalSlice";
import {
  setLoader
} from "../slice/bookingCreation/bookingCreationSlice";
import { baseStore, RootState } from "../store"; // Adjust the import based on your file structure
import {
  notifyLoadFailure,
  queryResultError,
} from "../helpers/failureNotification";
import { categoryFromSearch } from "../../modules/home/homeTiles";
import { categoryNoticeFor } from "../../api/apartment/categoryOutcome";
import { venueSearchWindow } from "./reservationWindow";
import { useSelectedLocationTimeZone } from "../../util/locationTimeZone";
import { DEFAULT_EVENT_FLOW_USE_CASE } from "../../modules/bookLocation/venueReservation/eventFlowUseCase";
const fetchVenuesByCityCall = async (
  useCase: string,
  city: string,
  fromDate: string,
  toDate: string,
  numberOfGuests: number,
  productCategory?: string
) => {
  const data = await baseStore.dispatch(
    apartmentBookingApiSlice.endpoints.getVenueByCityList.initiate({
      useCase,
      city: city,
      fromDate: fromDate,
      toDate: toDate,
      numberOfGuests: numberOfGuests,
      ...(productCategory ? { productCategory } : {}),
    })
  );
  return data;
};

const fetchVenuesByCityAndDistrictCall = async (
  useCase: string,
  city: string,
  district: string,
  fromDate: string,
  toDate: string,
  numberOfGuests: number,
  productCategory?: string
) => {
  const data = await baseStore.dispatch(
    apartmentBookingApiSlice.endpoints.getVenueByCityAndDistrictList.initiate({
      useCase,
      city: city,
      district: district,
      fromDate: fromDate,
      toDate: toDate,
      numberOfGuests: numberOfGuests,
      ...(productCategory ? { productCategory } : {}),
    })
  );
  return data;
};

/**
 * The event-flow room search. `useCase` is the flow's own (venue, or hall,
 * which runs the same flow); venue when a caller names none, as before.
 */
export const useFetchVenues = (useCase: string = DEFAULT_EVENT_FLOW_USE_CASE) => {
  const dispatch = useDispatch();

  const roomRentalLocation = useSelector(
    (state: RootState) => state.bookingCreation.roomRentalLocation
  );
  const checkInDate = useSelector(
    (state: RootState) => state.bookingCreation.orderData.checkInDate
  );
  const checkOutDate = useSelector(
    (state: RootState) => state.bookingCreation.orderData.checkOutDate
  );
  const startTime = useSelector(
    (state: RootState) => state.bookingCreation.orderData.startTime
  );
  const endTime = useSelector(
    (state: RootState) => state.bookingCreation.orderData.endTime
  );
  const numberOfGuests = useSelector(
    (state: RootState) => state.apartment.numberOfGuest
  );
  // The search area's clock (#3219) — the server reads the window there.
  const timeZone = useSelectedLocationTimeZone();

  const fetchVenues = async () => {
    try {
      dispatch(setLoader(true));
      dispatch(apartmentsLoadStarted());
      let apartments;
      let data;

      // The search asks for exactly the window creation and the quote send —
      // station day + station time (`venueSearchWindow`). It used to stamp the
      // times onto the PHONE's day, so abroad it searched one window and
      // booked another. Inside the `try`: `setTimeOnDate` throws on a time it
      // cannot read, which must surface as a failed search, not a crash.
      const searchWindow = venueSearchWindow({
        checkInDate,
        checkOutDate,
        startTime,
        endTime,
      }, timeZone);

      // ADR-0026 §A: which category's tile the customer tapped, carried in the
      // URL so a refresh or a shared link still shows the same catalogue —
      // the same contract the other entry parameters use. Absent for every
      // tenant that has no categories, and the search then behaves as it
      // always has.
      const productCategory = categoryFromSearch(window.location.search);

      if (roomRentalLocation.district === "all") {
        data = await fetchVenuesByCityCall(
          useCase,
          roomRentalLocation.city!,
          searchWindow.fromDate,
          searchWindow.toDate,
          numberOfGuests,
          productCategory
        );
      } else {
        data = await fetchVenuesByCityAndDistrictCall(
          useCase,
          roomRentalLocation.city!,
          roomRentalLocation.district!,
          searchWindow.fromDate,
          searchWindow.toDate,
          numberOfGuests,
          productCategory
        );
      }
      // `initiate` resolves with `{ error }` instead of throwing, so a failed
      // search rendered as "no venues available" (#1970). Return before
      // `setAllApartment`, which would clear the failure flag again.
      const failure = queryResultError(data);
      if (failure) {
        Logger.for("fetchVenues").error(failure);
        ErrorTracker.trackException(failure as Error);
        dispatch(apartmentsLoadFailed());
        notifyLoadFailure(dispatch, `book_location.error.fetch_${useCase}s`);
        dispatch(setLoader(false));
        return undefined;
      }
      if (data) {
        // `{ items, categoryNotice }` since #2764 F9/F10 — the list plus what
        // the server said about the `category` key (`categoryOutcome.ts`).
        apartments = data.data?.items;
      }

      // A result that carries no LIST is a failed search, not an empty one.
      //
      // QA #2793: the request returns 200 with seven venues and the screen
      // says "0 Veranstaltungsräume gefunden". `data.data` is undefined on the
      // hydration path although the HTTP call succeeded, and the slice's
      // defensive branch then stores `[]` — so a failure renders as the
      // no-results screen, which is the one thing CLAUDE.md says a failure
      // must never do. A spinner reads as unfinished; "0 gefunden" reads as
      // true, and the customer forwards a link that lies.
      //
      // The mechanism #2793 hit, since found: the account listener evicted
      // this cache when a stored session resolved mid-search, and `initiate`
      // then read an empty entry (`redux/accountScopedCaches.ts`). That is
      // fixed at its source; this guard stays, because any future eviction or
      // reset produces the same shape, and "I have no list" and "there is
      // nothing here" must reach the customer as different screens. Routing it
      // through `apartmentsLoadFailed` also keeps the search ANSWERED (#2791),
      // so this cannot bring the hang back.
      if (!Array.isArray(apartments)) {
        Logger.for("fetchVenues").error(
          "search resolved without a list",
          data
        )
        dispatch(apartmentsLoadFailed());
        notifyLoadFailure(dispatch, `book_location.error.fetch_${useCase}s`);
        dispatch(setLoader(false));
        return undefined;
      }
      // Reached only with a real list now, so this always answers the search
      // (#2791: a `if (apartments)` guard here once left an exit path that
      // answered nothing and hung the step on its spinner forever).
      //
      // CORRECTION to what that fix claimed: it said the guarded dispatch was
      // also the asymmetry between this flow and the apartment one. It was
      // not. `fetchApartmentsFlow` never had the guard and fails on the deep
      // link identically (#2793) — a cause one flow does not have cannot
      // explain a failure it does have. The guard explained the HANG, nothing
      // more; the empty list is the separate defect the branch above turns
      // from a silent lie into a visible failure.
      // Read from the store at call time, not from this hook's render: the
      // page records the flag in a mount effect, which the first search can
      // otherwise overtake.
      dispatch(
        categoryNoticeSet(
          categoryNoticeFor(
            data?.data?.categoryNotice,
            baseStore.getState().apartment.categoryRejectedOnEntry
          )
        )
      );
      dispatch(setAllApartment(apartments));
      dispatch(setLoader(false));
      return apartments;
    } catch (error) {
      // Was "fetchApartments" — a copy-paste that files every venue crash
      // under the apartment flow's name, in the one log line that would have
      // told QA which flow died.
      Logger.for("fetchVenues").error(error);
      dispatch(setLoader(false));
      ErrorTracker.trackException(error as Error);
      dispatch(apartmentsLoadFailed());
      notifyLoadFailure(dispatch, `book_location.error.fetch_${useCase}s`);
    }
  };

  return fetchVenues;
};
